import { categorySchema } from '@alicerce/contracts';
import { Alert, Button, Card, CardBody, EmptyState, Field, Input, PageHeader, PageLoader, Select } from '@alicerce/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { errorMessage } from '@/lib/api';
import { useZodForm } from '@/lib/form';
import { useApi, useCurrent } from '@/lib/session';
import { flattenCategories, type CategoryNode } from './types';

export function CategoriesPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { can } = useCurrent();
  const editable = can('catalog:write');
  const q = useQuery({ queryKey: ['categories'], queryFn: () => api.get<CategoryNode[]>('/v1/core/categories') });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['categories'] });
  const form = useZodForm(categorySchema, { name: '', parentId: null as string | null });
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const rename = useMutation({ mutationFn: (c: { id: string; name: string }) => api.patch(`/v1/core/categories/${c.id}`, { name: c.name }), onSuccess: () => (setEditing(null), refresh()) });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/v1/core/categories/${id}`), onSuccess: refresh });

  const onSubmit = form.submit(async (data) => {
    await api.post('/v1/core/categories', data);
    form.setValues({ name: '', parentId: data.parentId ?? null });
    await refresh();
  });

  const flat = flattenCategories(q.data ?? []);
  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <PageHeader title="Categorias" description="Organizam a vitrine da loja e os filtros de produtos." />
      {editable && (
        <Card>
          <CardBody>
            <form className="flex flex-wrap items-end gap-2" onSubmit={onSubmit} noValidate>
              <Field label="Nova categoria" error={form.errors.name} className="min-w-48 flex-1">
                {(a) => <Input {...a} value={form.values.name} onChange={(e) => form.set('name', e.target.value)} />}
              </Field>
              <Field label="Dentro de" className="w-48">
                {(a) => (
                  <Select {...a} value={form.values.parentId ?? ''} onChange={(e) => form.set('parentId', e.target.value || null)}>
                    <option value="">Nenhuma (principal)</option>
                    {flat.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Button type="submit" loading={form.submitting}>
                Adicionar
              </Button>
            </form>
            {form.formError && (
              <Alert tone="danger" className="mt-3">
                {form.formError}
              </Alert>
            )}
          </CardBody>
        </Card>
      )}
      <Card>
        {q.isPending ? (
          <PageLoader />
        ) : flat.length === 0 ? (
          <EmptyState title="Nenhuma categoria" description="Crie categorias como Camisetas, Acessórios, Promoções." />
        ) : (
          <ul className="divide-y divide-line">
            {flat.map((c) => (
              <li key={c.id} className="flex items-center gap-2 px-4 py-2 text-sm">
                {editing?.id === c.id ? (
                  <form
                    className="flex flex-1 gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      rename.mutate(editing);
                    }}
                  >
                    <Input aria-label="Nome da categoria" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} autoFocus />
                    <Button type="submit" size="sm" loading={rename.isPending}>
                      Salvar
                    </Button>
                  </form>
                ) : (
                  <span className="flex-1">{c.label}</span>
                )}
                {editable && editing?.id !== c.id && (
                  <>
                    <Button size="sm" variant="ghost" aria-label={`Renomear ${c.label}`} onClick={() => setEditing({ id: c.id, name: c.label.replace(/^(— )+/, '') })}>
                      <Pencil className="size-4" />
                    </Button>
                    <Button size="sm" variant="ghost" aria-label={`Apagar ${c.label}`} onClick={() => remove.mutate(c.id)}>
                      <Trash2 className="size-4" />
                    </Button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        {(rename.isError || remove.isError) && (
          <Alert tone="danger" className="m-3">
            {errorMessage(rename.error ?? remove.error)}
          </Alert>
        )}
      </Card>
    </div>
  );
}
